/**
 * WorkspaceView — Plans & Takeoffs: the 44 px Recent bar, the 64 px tool
 * rail, the drawing canvas, and the 390 px inspector. Owns viewer state
 * (open drawing/page, tool, selection, clipboard, snap), scale application,
 * page rotation (marks rotate with the ink), auto-name runs, and the
 * addendum overlay.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { FileWarning, FolderOpen } from "lucide-react";
import { useTakeoff } from "./store";
import { sheetLabel } from "./sheetLabel";
import { getPageState, pageKey, rotatePoints } from "./pageState";
import { measureQuantity, feetPerUnitFromPreset } from "./geometry";
import { isPdf, isTiff, isPreviewable, fileExt, CARD, INPUT, BTN_ORANGE, BTN_GHOST, LABEL } from "./theme";
import ToolRail from "./ToolRail";
import DrawingCanvas from "./DrawingCanvas";
import Inspector from "./Inspector";
import SheetGallery from "./SheetGallery";
import { Overlay, DialogTitle } from "./dialogs";
import { openPdfDoc, pageTextSpans } from "./pdfSetup";
import { prepareDrawing, readRegion, ocrRegion } from "./api";
import { buildFolderSheets } from "./sheets";
import FolderSheetList from "./FolderSheetList";
import AutoNamePreview from "./AutoNamePreview";
import { useCadInfo } from "./LayersPanel";
import { cadPrepare, cadRemoveEdit } from "./api";
import CadProgress from "./CadProgress";
import CommandBar from "./CommandBar";
import LayerDrawer from "./LayerDrawer";
import { Loader2 } from "lucide-react";

export default function WorkspaceView({ onContext, initialDrawingId = null }) {
  const { doc, mutate, activeProject, drawings, pushRecent, recents, projectLoading, jobItems, undo, canUndo, saveLastSheet, uploadFiles, refreshDrawings, folders, viewer } = useTakeoff();
  const [activeDrawing, setActiveDrawing] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [tool, setTool] = useState("select");
  const [activeItemId, setActiveItemId] = useState(null);
  const [selection, setSelection] = useState([]);
  const [clipboard, setClipboard] = useState(null);
  const [snap, setSnap] = useState(true);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [folderListOpen, setFolderListOpen] = useState(false);
  const [autoNaming, setAutoNaming] = useState(false);
  const [autoNameScope, setAutoNameScope] = useState("drawing"); // "drawing" | "folder"
  const [autoNameBusy, setAutoNameBusy] = useState("");
  const [scalePrompt, setScalePrompt] = useState(null); // {p1, p2}
  const [overlay, setOverlay] = useState(null);
  // Right panel (Inspector): phones start with it CLOSED so the drawing gets
  // the whole screen; a small Plans button reopens it. Portrait + landscape.
  const [panelOpen, setPanelOpen] = useState(() => (typeof window === "undefined" ? true : window.innerWidth >= 768));
  const isPhone = () => typeof window !== "undefined" && window.innerWidth < 768;
  const dimsRef = useRef({ unitH: 1000, paperIn: 12 });
  const textCache = useRef(new Map()); // "fileId:page" -> {spans, width, height}

  const pageState = useMemo(
    () => (activeDrawing && doc ? getPageState(doc, activeDrawing.drawing_id, pageNum) : null),
    [doc, activeDrawing, pageNum]
  );
  const pageItems = useMemo(
    () => (doc?.items || []).filter((i) => i.fileId === activeDrawing?.drawing_id && i.page === pageNum),
    [doc, activeDrawing, pageNum]
  );
  const activeItem = useMemo(
    () => jobItems.find((i) => i.item_id === activeItemId) || null,
    [jobItems, activeItemId]
  );
  const canvasActions = useRef(null);

  // ---------- CAD (DWG/DXF) ----------
  const [cad, setCad] = useCadInfo(activeDrawing);
  const [cadLayer, setCadLayer] = useState("0"); // layer new CAD entities are drawn on
  // drawing settings: dimension format/size, grid + ortho lock, symbol to stamp
  const [cadDraw, setCadDraw] = useState({ dimFormat: "ftin", dimHeight: 2.5, grid: 0, ortho: false, symbol: "manhole" });
  const cadExt = activeDrawing && ["dwg", "dxf"].includes(fileExt(activeDrawing.name));
  // keep the open drawing in sync with the live list (conversion finishes,
  // layers re-render -> mime/pages/render_version change -> viewer reloads)
  useEffect(() => {
    if (!activeDrawing) return;
    const live = drawings.find((d) => d.drawing_id === activeDrawing.drawing_id);
    if (live && live !== activeDrawing && (live.mime !== activeDrawing.mime || live.pages !== activeDrawing.pages
        || (live.cad?.render_version || 0) !== (activeDrawing.cad?.render_version || 0) || live.cad?.status !== activeDrawing.cad?.status)) {
      setActiveDrawing(live);
    }
  }, [drawings, activeDrawing]);
  // a CAD upload that was never converted (older uploads) converts on open
  const kickedRef = useRef(new Set());
  useEffect(() => {
    if (!activeDrawing || !cadExt || viewer || activeDrawing.cad || kickedRef.current.has(activeDrawing.drawing_id)) return;
    kickedRef.current.add(activeDrawing.drawing_id);
    cadPrepare(activeDrawing.drawing_id).then(() => refreshDrawings?.()).catch(() => {});
  }, [activeDrawing, cadExt, viewer, refreshDrawings]);
  // conversion / re-render finished -> refresh the list so the viewer reloads
  const lastCadStatus = useRef(null);
  useEffect(() => {
    const st = cad?.status || activeDrawing?.cad?.status || null;
    if (lastCadStatus.current && lastCadStatus.current !== "ready" && st === "ready") refreshDrawings?.();
    lastCadStatus.current = st;
  }, [cad?.status, activeDrawing?.cad?.status, refreshDrawings]);
  // CAD sheets know their own scale: apply it the first time a page opens
  useEffect(() => {
    if (!activeDrawing || !cad || !doc) return;
    const pg = cad.pages?.find((p) => p.page === pageNum);
    if (!pg?.feet_per_unit) return;
    const key = pageKey(activeDrawing.drawing_id, pageNum);
    if (doc.pages?.[key]?.feetPerUnit) return;
    mutate((d) => {
      const page = d.pages[key] || (d.pages[key] = {});
      page.feetPerUnit = pg.feet_per_unit;
      page.scaleLabel = `CAD ${pg.layout}`;
    });
  }, [activeDrawing, cad, pageNum, doc, mutate]);

  const openDrawing = useCallback((drawing, page = 1) => {
    setActiveDrawing(drawing);
    setPageNum(Math.max(1, page));
    setSelection([]);
    setTool("select");
    setOverlay(null);
    // phones: hand the whole screen to the drawing once one is picked
    if (isPhone()) setPanelOpen(false);
  }, []);

  // recent sheets + header context line
  useEffect(() => {
    if (!activeDrawing || !pageState) { onContext?.(""); return; }
    pushRecent({ fileId: activeDrawing.drawing_id, page: pageNum, name: pageState.name, drawingName: activeDrawing.name });
    onContext?.(`${activeDrawing.name} · ${pageState.name}`);
  }, [activeDrawing, pageNum, pageState?.name]); // eslint-disable-line

  // persist the viewed sheet (server-backed, per user) whenever it changes
  useEffect(() => {
    if (activeDrawing) saveLastSheet(activeDrawing.drawing_id, pageNum);
  }, [activeDrawing, pageNum, saveLastSheet]);

  // reopen the job on the sheet THIS user last viewed; if that file/page is
  // gone, fall back to the first available drawing sheet — only a job with
  // no drawings shows the empty state
  const didRestore = useRef(false);
  useEffect(() => {
    if (didRestore.current || activeDrawing || !activeProject || projectLoading || !drawings.length) return;
    didRestore.current = true;
    const ls = activeProject.last_sheet;
    const target = (initialDrawingId && drawings.find((d) => d.drawing_id === initialDrawingId))
      || (ls && drawings.find((d) => d.drawing_id === ls.file_id))
      || drawings.find((d) => isPreviewable(d))
      || drawings[0];
    if (target) openDrawing(target, ls && target.drawing_id === ls.file_id ? (ls.page || 1) : 1);
  }, [activeDrawing, activeProject, projectLoading, drawings, openDrawing, initialDrawingId]);

  const onDims = useCallback((unitH, paperIn) => { dimsRef.current = { unitH, paperIn }; }, []);

  // ---------- folder-wide sheet browsing ----------
  // The arrows, position pill and sheet list all walk ONE flat, naturally
  // sorted list of the folder's sheets — crossing file boundaries (the last
  // page of one PDF rolls into the first page of the next). The Plans root
  // acts as a pseudo-folder so loose files browse the same way.
  const activeFolderId = activeDrawing ? (activeDrawing.folder_id || null) : null;
  const sheets = useMemo(
    () => (activeDrawing ? buildFolderSheets(drawings, doc, activeFolderId, activeDrawing.category || "plans") : []),
    [activeDrawing, drawings, doc, activeFolderId]
  );
  const sheetIndex = useMemo(
    () => (activeDrawing ? sheets.findIndex((s) => s.drawing.drawing_id === activeDrawing.drawing_id && s.page === pageNum) : -1),
    [sheets, activeDrawing, pageNum]
  );
  const goSheet = useCallback((idx) => {
    const s = sheets[idx];
    if (!s) return;
    if (s.drawing.drawing_id === activeDrawing?.drawing_id) setPageNum(s.page);
    else openDrawing(s.drawing, s.page);
  }, [sheets, activeDrawing, openDrawing]);
  const sheetNav = useMemo(() => {
    if (!activeDrawing || sheetIndex < 0 || !sheets.length) return null;
    return {
      index: sheetIndex,
      total: sheets.length,
      name: sheets[sheetIndex].label,
      onPrev: () => goSheet(sheetIndex - 1),
      onNext: () => goSheet(sheetIndex + 1),
      onJump: (idx) => goSheet(idx),
      onOpenList: () => setFolderListOpen(true),
    };
  }, [activeDrawing, sheetIndex, sheets, goSheet]);

  // Cache page counts for the open folder's PDFs (server-side, idempotent)
  // so multipage files contribute ALL their sheets to folder browsing.
  const preparedRef = useRef(new Set());
  useEffect(() => {
    if (!activeDrawing) return;
    const fid = activeDrawing.folder_id || null;
    const cat = activeDrawing.category || "plans";
    const pending = drawings.filter((d) => (d.folder_id || null) === fid && (d.category || "plans") === cat && (isPdf(d) || isTiff(d)) && !d.pages && !preparedRef.current.has(d.drawing_id));
    if (!pending.length) return;
    pending.forEach((d) => preparedRef.current.add(d.drawing_id));
    (async () => {
      let changed = false;
      for (const d of pending) {
        try { await prepareDrawing(d.drawing_id); changed = true; }
        catch { preparedRef.current.delete(d.drawing_id); }
      }
      if (changed) refreshDrawings();
    })();
  }, [activeDrawing, drawings, refreshDrawings]);

  const activeFolderName = useMemo(() => {
    if (!activeDrawing) return "";
    if (!activeDrawing.folder_id) return "Plans (root)";
    return folders.find((f) => f.folder_id === activeDrawing.folder_id)?.name || "Folder";
  }, [activeDrawing, folders]);

  const renameSheetPage = useCallback((drawingId, page, name) => {
    mutate((d) => {
      const key = pageKey(drawingId, page);
      const cur = d.pages[key] || (d.pages[key] = {});
      cur.name = name;
    });
    toast.success("Sheet renamed");
  }, [mutate]);

  // ---------- scale ----------
  const applyScale = useCallback((fpu, label) => {
    if (!activeDrawing || !(fpu > 0)) return;
    let recalc = 0;
    mutate((d) => {
      const key = pageKey(activeDrawing.drawing_id, pageNum);
      const pg = d.pages[key] || (d.pages[key] = {});
      pg.feetPerUnit = fpu;
      pg.scaleLabel = label;
      for (const it of d.items) {
        if (it.fileId !== activeDrawing.drawing_id || it.page !== pageNum) continue;
        if (!selection.includes(it.id)) continue;
        if (it.type === "linear" || it.type === "area") { it.quantity = measureQuantity(it.type, it.points, fpu); recalc++; }
      }
    });
    toast.success(recalc
      ? `Scale set to ${label} — recalculated ${recalc} selected takeoff${recalc > 1 ? "s" : ""}`
      : `Scale set to ${label}`);
  }, [activeDrawing, pageNum, selection, mutate]);

  const applyPreset = useCallback((ratio, label) => {
    applyScale(feetPerUnitFromPreset(ratio, dimsRef.current.paperIn), label);
  }, [applyScale]);

  const confirmCustomScale = useCallback((feet) => {
    const { p1, p2 } = scalePrompt || {};
    setScalePrompt(null);
    const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
    if (!dist || !(feet > 0)) return;
    applyScale(feet / dist, `Custom · ${feet} ft reference`);
  }, [scalePrompt, applyScale]);

  // ---------- rotation (marks rotate with the ink) ----------
  const rotatePage = useCallback((dir) => {
    if (!activeDrawing || !doc) return;
    const oldUH = dimsRef.current.unitH || 1000;
    const newUH = 1_000_000 / oldUH;
    const id = activeDrawing.drawing_id;
    mutate((d) => {
      const key = pageKey(id, pageNum);
      const pg = d.pages[key] || (d.pages[key] = {});
      pg.rotation = (((pg.rotation || 0) + dir * 90) % 360 + 360) % 360;
      pg.highlights = (pg.highlights || []).map((h) => {
        const [tl] = rotatePoints([{ x: h.x, y: h.y }], oldUH, newUH, dir);
        const [br] = rotatePoints([{ x: h.x + h.w, y: h.y + h.h }], oldUH, newUH, dir);
        return { ...h, x: Math.min(tl.x, br.x), y: Math.min(tl.y, br.y), w: Math.abs(br.x - tl.x), h: Math.abs(br.y - tl.y) };
      });
      for (const it of d.items) {
        if (it.fileId !== id || it.page !== pageNum) continue;
        it.points = rotatePoints(it.points, oldUH, newUH, dir);
      }
    });
  }, [activeDrawing, doc, pageNum, mutate]);

  // ---------- auto name sheets ----------
  const pageText = useCallback(async (fileId, page) => {
    const key = `${fileId}:${page}`;
    if (!textCache.current.has(key)) {
      const rec = drawings.find((d) => d.drawing_id === fileId);
      // TIFF drawings are pure raster — no text layer; search just skips them
      // (Auto Name still works via the server-side OCR reader)
      if (rec && isTiff(rec)) {
        textCache.current.set(key, { spans: [], width: 1, height: 1 });
        return textCache.current.get(key);
      }
      const docProxy = await openPdfDoc(fileId);
      const rot = getPageState(doc, fileId, page).rotation || 0;
      textCache.current.set(key, await pageTextSpans(docProxy, page, rot));
    }
    return textCache.current.get(key);
  }, [doc, drawings]);

  // Auto Name reads the selected title-block region SERVER-SIDE (pdfplumber
  // word extraction — rotation-aware and far more reliable than browser text
  // spans), then shows every proposed name for review before applying.
  const [autoNameRows, setAutoNameRows] = useState(null);
  const autoNameRectRef = useRef(null);

  const runAutoName = useCallback(async (rect) => {
    setAutoNaming(false);
    if (!activeDrawing || !doc) return;
    // "folder" scope (Auto Name Folder from the sheet list): the same box is
    // read on every page of EVERY PDF in the folder; "drawing" scope (sheets
    // gallery) names just the open PDF.
    const scope = autoNameScope;
    const nameable = (d) => isPdf(d) || isTiff(d);
    const targets = scope === "folder"
      ? [...new Map(sheets.filter((s) => nameable(s.drawing)).map((s) => [s.drawing.drawing_id, s.drawing])).values()]
      : (nameable(activeDrawing) ? [activeDrawing] : []);
    if (!targets.length) { toast.error("No nameable drawings in this folder."); return; }
    const uh = dimsRef.current.unitH || 1000;
    const norm = {
      x0: rect.x / 1000, y0: rect.y / uh,
      x1: (rect.x + rect.w) / 1000, y1: (rect.y + rect.h) / uh,
    };
    autoNameRectRef.current = norm;
    const rows = [];
    try {
      for (const target of targets) {
        let nPages = 1;
        try { nPages = Math.max(1, (await prepareDrawing(target.drawing_id)).pages || 1); } catch { /* 1 */ }
        for (let pg = 1; pg <= nPages; pg++) {
          setAutoNameBusy(targets.length > 1
            ? `Reading ${target.name} — page ${pg} of ${nPages}…`
            : `Reading page ${pg} of ${nPages}…`);
          try {
            const r = await readRegion(target.drawing_id, pg, norm);
            rows.push({ fileId: target.drawing_id, drawingName: target.name, page: pg, text: r.text || "", hasText: r.has_text_layer !== false, proposed: r.proposed || "", source: "text" });
          } catch {
            rows.push({ fileId: target.drawing_id, drawingName: target.name, page: pg, text: "", hasText: true, proposed: "", source: "text" });
          }
        }
      }
    } finally {
      setAutoNameBusy("");
    }
    if (rows.length && !rows.some((r) => r.proposed) && rows.every((r) => !r.hasText)) {
      toast.message("No readable text layer in that region — use the AI reader in the preview.");
    }
    setAutoNameRows(rows);
  }, [activeDrawing, doc, autoNameScope, sheets]);

  const ocrAutoNamePage = useCallback(async (fileId, pg) => {
    const res = await ocrRegion(fileId, pg, autoNameRectRef.current);
    setAutoNameRows((rows) => (rows || []).map((r) => (
      r.fileId === fileId && r.page === pg
        ? { ...r, proposed: res.proposed || r.proposed, text: res.text || r.text, source: "vision", hasText: true }
        : r)));
    return res;
  }, []);

  const applyAutoNames = useCallback((chosen) => {
    if (!chosen.length) { setAutoNameRows(null); return; }
    mutate((d) => {
      for (const c of chosen) {
        const key = pageKey(c.fileId, c.page);
        const cur = d.pages[key] || (d.pages[key] = {});
        cur.name = c.name;
      }
    });
    toast.success(`Named ${chosen.length} sheet${chosen.length > 1 ? "s" : ""}`);
    setAutoNameRows(null);
  }, [mutate]);

  // ---- drag & drop upload zone hooks (spec §9) — must live above the
  // loading early-return; handler functions are below it ----
  const [dropActive, setDropActive] = useState(false);
  const dragDepth = useRef(0);
  // never let Safari navigate away to a dropped file, inside or outside the zone
  useEffect(() => {
    const stop = (e) => { if (Array.from(e.dataTransfer?.types || []).includes("Files")) e.preventDefault(); };
    window.addEventListener("dragover", stop);
    window.addEventListener("drop", stop);
    return () => { window.removeEventListener("dragover", stop); window.removeEventListener("drop", stop); };
  }, []);

  // ---------- measured-total footer ----------
  if (projectLoading || !doc) {
    return <div className="flex min-h-[400px] items-center justify-center text-sm text-slate-400" data-testid="takeoff-loading">Loading job…</div>;
  }

  // the whole viewer area accepts files/folders/ZIPs dragged from Finder —
  // same pipeline as the upload buttons
  const dragHasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
  /** Safari-compatible directory traversal: folders become {file, path}
   * entries so the same hierarchy rules as ZIP uploads apply. */
  const collectDropped = async (dt) => {
    const out = [];
    const walk = (entry, prefix) => new Promise((resolve) => {
      if (entry.isFile) {
        entry.file((f) => { out.push({ file: f, path: prefix + f.name }); resolve(); }, () => resolve());
      } else if (entry.isDirectory) {
        const reader = entry.createReader();
        const readAll = () => reader.readEntries(async (ents) => {
          if (!ents.length) return resolve();
          for (const ent of ents) await walk(ent, `${prefix}${entry.name}/`);
          readAll();
        }, () => resolve());
        readAll();
      } else resolve();
    });
    const entries = [];
    for (const item of dt.items || []) {
      const entry = item.webkitGetAsEntry?.();
      if (entry) entries.push(entry);
    }
    for (const entry of entries) await walk(entry, "");
    if (!out.length) out.push(...Array.from(dt.files || []).map((f) => ({ file: f, path: "" })));
    return out;
  };
  const onDropFiles = async (e) => {
    if (viewer) return; // view-only: drops never upload
    if (!dragHasFiles(e)) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = 0;
    setDropActive(false);
    const files = await collectDropped(e.dataTransfer);
    if (files.length) uploadFiles(files);
  };

  const cadReady = !!(activeDrawing?.cad && activeDrawing.cad.status === "ready" && activeDrawing.cad.pdf);
  const previewable = activeDrawing && (isPreviewable(activeDrawing) || cadReady);
  const cadLike = activeDrawing && !previewable;
  const cadStatus = activeDrawing?.cad?.status || cad?.status;
  // recents pointing at deleted drawings fall back safely — they're hidden
  const liveRecents = recents.filter((r) => drawings.some((d) => d.drawing_id === r.fileId));

  return (
    <div className="flex min-h-[650px] flex-col" style={{ height: "calc(100dvh - 220px)" }} data-testid="takeoff-workspace">
      {/* recent sheets bar */}
      <div className="flex h-11 shrink-0 items-center gap-1 overflow-x-auto border-b border-[#24324a] bg-[#0b172a] px-3" data-testid="takeoff-recent-bar">
        <span className="mr-2 shrink-0 text-[10px] font-semibold uppercase tracking-widest text-white/40">Recent</span>
        {liveRecents.length === 0 && <span className="text-xs text-white/35">Opened drawings will appear here.</span>}
        {liveRecents.map((r) => {
          const current = activeDrawing?.drawing_id === r.fileId && pageNum === r.page;
          const d = drawings.find((x) => x.drawing_id === r.fileId);
          // live sheet name — renames made after the visit still show here
          const liveName = doc?.pages?.[`${r.fileId}:${r.page}`]?.name || r.name;
          return (
            <button
              key={`${r.fileId}:${r.page}`}
              onClick={() => d && openDrawing(d, r.page)}
              title={`${r.drawingName} · page ${r.page}`}
              data-testid={`takeoff-recent-${r.fileId}-${r.page}`}
              className={`shrink-0 border-b-2 px-2 py-1 text-xs transition-colors ${current ? "border-[#f97316] text-white" : "border-transparent text-white/55 hover:text-white"}`}
            >
              {sheetLabel(liveName, r.page, r.drawingName)}
            </button>
          );
        })}
      </div>

      <div
        className="relative flex min-h-0 flex-1"
        onDragEnter={(e) => { if (!dragHasFiles(e)) return; e.preventDefault(); dragDepth.current += 1; setDropActive(true); }}
        onDragOver={(e) => { if (!dragHasFiles(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
        onDragLeave={() => { dragDepth.current -= 1; if (dragDepth.current <= 0) { dragDepth.current = 0; setDropActive(false); } }}
        onDrop={onDropFiles}
      >
        {dropActive && (
          <div className="pointer-events-none absolute inset-0 z-40 flex items-center justify-center rounded-lg border-2 border-dashed border-[#1a3fd1] bg-[#1a3fd1]/10 backdrop-blur-[1px]" data-testid="takeoff-drop-overlay">
            <p className="rounded-xl bg-[#0b0b0f] px-5 py-3 text-sm font-semibold text-white shadow-xl">Drop files here to upload</p>
          </div>
        )}
        <ToolRail
          tool={tool} setTool={setTool} hasDrawing={!!previewable}
          hasDraft={false} hasSelection={selection.length > 0} canPaste={!!clipboard?.items?.length}
          snap={snap}
          onRotate={rotatePage}
          onSelectAll={() => setSelection(pageItems.map((i) => i.id))}
          onCopy={() => canvasActions.current?.copy()}
          onPaste={() => canvasActions.current?.paste()}
          onUndoPoint={() => canvasActions.current?.undo()}
          onDeleteSelected={() => canvasActions.current?.deleteSel()}
          canUndo={canUndo}
          onUndo={undo}
          viewer={viewer}
          cad={cadReady}
        />

        {activeDrawing == null ? (
          <div className="flex flex-1 items-center justify-center p-8" style={{ background: "radial-gradient(1200px 800px at 50% 30%, #cbd4df, #aeb9c7)" }}>
            <div className={`${CARD} max-w-sm p-8 text-center`} data-testid="takeoff-no-drawing">
              <FolderOpen className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-3 text-sm font-semibold text-slate-700">No drawing open</p>
              <p className="mt-1 text-xs text-slate-400">{viewer ? "Open a sheet from the Plans tab on the right." : "Open a sheet from the Plans tab on the right, or upload drawings into this job."}</p>
            </div>
          </div>
        ) : cadLike ? (
          <div className="flex flex-1 items-center justify-center p-8" style={{ background: "radial-gradient(1200px 800px at 50% 30%, #cbd4df, #aeb9c7)" }}>
            <div className={`${CARD} max-w-sm p-8 text-center`} data-testid="takeoff-not-previewable">
              {cadExt && cadStatus !== "error" ? <Loader2 className="mx-auto h-8 w-8 animate-spin text-[#1a3fd1]" data-testid="cad-converting" /> : <FileWarning className="mx-auto h-8 w-8 text-amber-400" />}
              <p className="mt-3 text-sm font-semibold text-slate-700">{activeDrawing.name}</p>
              <p className="mt-1 text-xs text-slate-500" data-testid="cad-status-text">
                {cadExt
                  ? (cadStatus === "error"
                    ? `Could not read this CAD file: ${activeDrawing.cad?.error || "unknown error"}`
                    : (cad?.progress?.detail || (cadStatus === "rendering" ? "Drawing the sheets with their layers…" : "Reading the CAD file — layers, sheets and geometry. Large drawings take about a minute.")))
                  : fileExt(activeDrawing.name) && ["dwf", "rvt", "ifc", "skp"].includes(fileExt(activeDrawing.name))
                    ? "BIM files are stored and downloadable, but need to be exported to PDF or DWG for browser measuring."
                    : "This file type can't be measured in the browser — upload a PDF or image to take off against."}
              </p>
              {cadExt && cadStatus !== "error" && <CadProgress step={cad?.progress?.step || (cadStatus === "rendering" ? "drawing" : "reading")} pct={cad?.progress?.pct ?? (cadStatus === "rendering" ? 90 : 5)} />}
              {cadExt && cadStatus === "error" && !viewer && (
                <button onClick={() => cadPrepare(activeDrawing.drawing_id).then(() => refreshDrawings?.())} className={`${BTN_GHOST} mt-3 text-xs`} data-testid="cad-retry-btn">Try again</button>
              )}
            </div>
          </div>
        ) : (
          <div className="relative flex min-w-0 flex-1 flex-col">
          {cadExt && cadReady && <LayerDrawer drawing={activeDrawing} cad={cad} onCadChange={setCad} />}
          <DrawingCanvas
            drawing={activeDrawing}
            pageNum={pageNum}
            onPage={setPageNum}
            pageState={pageState}
            items={pageItems}
            tool={tool} setTool={setTool}
            activeItem={activeItem}
            snap={snap} onToggleSnap={() => setSnap((s) => !s)}
            selection={selection} setSelection={setSelection}
            clipboard={clipboard} setClipboard={setClipboard}
            mutate={mutate}
            autoNaming={autoNaming}
            autoNameScope={autoNameScope}
            onAutoNameRegion={runAutoName}
            onScalePoints={(p1, p2) => setScalePrompt({ p1, p2 })}
            onDims={onDims}
            overlay={overlay}
            onOpenGallery={() => setGalleryOpen(true)}
            actionBus={canvasActions}
            onUndo={undo}
            sheetNav={sheetNav}
            cad={cadReady ? cad : null}
            cadLayer={cadLayer}
            cadDraw={cadDraw}
            onCadChange={setCad}
          />
          {cadExt && (
            <CommandBar
              tool={tool} onTool={setTool}
              cadDraw={cadDraw} onCadDraw={setCadDraw}
              layers={(cad?.layers || []).map((l) => l.name)}
              cadLayer={cadLayer} onCadLayer={setCadLayer}
              onUndo={async () => {
                const page = String(pageNum);
                const edits = (cad?.edits || {})[page] || [];
                const last = edits[edits.length - 1];
                if (!last) { toast.message("Nothing to undo on this sheet"); return; }
                try {
                  await cadRemoveEdit(activeDrawing.drawing_id, last.id, pageNum);
                  setCad({ ...cad, status: "rendering", edits: { ...(cad.edits || {}), [page]: edits.slice(0, -1) } });
                } catch (e) { toast.error(e?.response?.data?.detail || "Could not undo"); }
              }}
              disabled={!cadReady || viewer}
            />
          )}
          </div>
        )}

        {panelOpen && (
          <Inspector
            drawing={activeDrawing}
            pageNum={pageNum}
            pageState={pageState}
            cad={activeDrawing?.cad ? cad : null}
            onCadChange={setCad}
            cadLayer={cadLayer}
            onCadLayer={setCadLayer}
            cadDraw={cadDraw}
            onCadDraw={setCadDraw}
            pageItems={pageItems}
            measurable={!!previewable}
            activeItemId={activeItemId}
            onPickItem={(id) => {
              setActiveItemId(id);
              const it = jobItems.find((i) => i.item_id === id);
              if (it) setTool(it.tool || "linear");
            }}
            selection={selection} setSelection={setSelection}
            applyPreset={applyPreset}
            startCustomScale={() => setTool("scale")}
            openDrawing={openDrawing}
            overlay={overlay} setOverlay={setOverlay}
            pageText={pageText}
            onClose={() => setPanelOpen(false)}
          />
        )}

        {/* reopen handle when the panel is closed — floats over the drawing */}
        {!panelOpen && (
          <button
            onClick={() => setPanelOpen(true)}
            data-testid="inspector-open-btn"
            className="absolute bottom-4 right-4 z-30 flex items-center gap-1.5 rounded-full bg-[#0b172a] px-4 py-2 text-[10px] font-semibold uppercase tracking-widest text-white shadow-lg hover:bg-[#1a3fd1]"
          >
            <FolderOpen className="h-3.5 w-3.5" /> Plans
          </button>
        )}

        {/* sheet gallery stays INSIDE the workspace — the module header and
            job context remain visible, and closing returns to the exact
            viewer state (tool, zoom, pan, selection are never unmounted) */}
        {galleryOpen && activeDrawing && (isPdf(activeDrawing) || isTiff(activeDrawing)) && (
          <SheetGallery
            drawing={activeDrawing}
            docState={doc}
            currentPage={pageNum}
            onOpenPage={(pg) => setPageNum(pg)}
            onClose={() => setGalleryOpen(false)}
            onAutoName={() => { setGalleryOpen(false); setAutoNameScope("drawing"); setAutoNaming(true); setTool("select"); }}
            onRename={(pg, name) => renameSheetPage(activeDrawing.drawing_id, pg, name)}
            pageText={pageText}
            mutate={mutate}
          />
        )}

        {/* folder sheet list — flat, scrollable, crosses file boundaries */}
        {folderListOpen && activeDrawing && (
          <FolderSheetList
            folderName={activeFolderName}
            sheets={sheets}
            activeKey={`${activeDrawing.drawing_id}:${pageNum}`}
            doc={doc}
            onJump={(idx) => { goSheet(idx); }}
            onRename={renameSheetPage}
            onAutoNameFolder={sheets.some((s) => isPdf(s.drawing) || isTiff(s.drawing))
              ? () => { setFolderListOpen(false); setAutoNameScope("folder"); setAutoNaming(true); setTool("select"); }
              : null}
            onClose={() => setFolderListOpen(false)}
          />
        )}

        {/* auto name preview — review/edit proposed sheet names, AI reader
            for pages without a text layer */}
        {autoNameRows && (
          <AutoNamePreview
            rows={autoNameRows}
            onApply={applyAutoNames}
            onOcr={ocrAutoNamePage}
            onClose={() => setAutoNameRows(null)}
          />
        )}
      </div>

      {/* custom scale prompt */}
      <Overlay open={!!scalePrompt} onClose={() => setScalePrompt(null)}>
        <DialogTitle onClose={() => setScalePrompt(null)}>Set custom scale</DialogTitle>
        <ScalePromptBody onConfirm={confirmCustomScale} />
      </Overlay>

      {/* auto-name busy indicator */}
      {autoNameBusy && (
        <div className="fixed bottom-6 left-1/2 z-[150] -translate-x-1/2 rounded-full bg-[#0b172a] px-4 py-2 text-xs font-semibold text-white shadow-xl" data-testid="autoname-progress">
          {autoNameBusy}
        </div>
      )}
    </div>
  );
}

function ScalePromptBody({ onConfirm }) {
  const [feet, setFeet] = useState("");
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">How long is the dimension you just clicked, in feet? (e.g. 20 for 20 ft, or 12.5)</p>
      <div>
        <label className={LABEL}>Known length (ft)</label>
        <input autoFocus type="number" min="0" step="0.01" value={feet} onChange={(e) => setFeet(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && Number(feet) > 0) onConfirm(Number(feet)); }}
          className={INPUT} data-testid="scale-prompt-feet" />
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button className={BTN_ORANGE} disabled={!(Number(feet) > 0)} onClick={() => onConfirm(Number(feet))} data-testid="scale-prompt-apply">Apply scale</button>
      </div>
    </div>
  );
}
